import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { StyleSheet, css } from 'aphrodite';
import logo from '../../logo192.png';
import { LINKS, openExternalLink } from '../../config/links';
import { colors } from '../../styles/theme';

const navItems = [
  { label: 'Customers', to: '/customers' },
  { label: 'Restaurants', to: '/restaurants' },
  { label: 'Riders', to: '/riders' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const goHome = () => {
    closeMenu();
    navigate('/');
  };

  const openAction = (url) => {
    closeMenu();
    openExternalLink(url);
  };

  return (
    <header className={css(styles.header)}>
      <div className={css(styles.navbar)}>
        <button className={css(styles.logoButton)} onClick={goHome}>
          <span className={css(styles.logoMark)}>
            <img
              src={logo}
              alt="YaBatho Logo"
              className={css(styles.logoImage)}
            />
          </span>
          <span>
            <span className={css(styles.logoText)}>YaBatho</span>
            <span className={css(styles.logoSubtext)}>Delivery Platform</span>
          </span>
        </button>

        <button
          className={css(styles.menuButton)}
          type="button"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className={css(styles.menuLine, menuOpen && styles.menuLineTop)} />
          <span className={css(styles.menuLine, menuOpen && styles.menuLineMiddle)} />
          <span className={css(styles.menuLine, menuOpen && styles.menuLineBottom)} />
        </button>

        <div className={css(styles.desktopNav)}>
          <nav className={css(styles.navLinks)} aria-label="Main navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  css(styles.navLink, isActive && styles.navLinkActive)
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={css(styles.navActions)}>
            <button
              className={css(styles.ghostButton)}
              onClick={() => openAction(LINKS.adminDashboard)}
            >
              Admin
            </button>
            <button
              className={css(styles.primaryButton)}
              onClick={() => openAction(LINKS.customerWebApp)}
            >
              Order Now
            </button>
          </div>
        </div>
      </div>

      <div
        className={css(styles.mobileMenu, menuOpen && styles.mobileMenuOpen)}
        aria-hidden={!menuOpen}
      >
        <nav
          className={css(styles.mobileNavLinks)}
          aria-label="Mobile navigation"
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              className={({ isActive }) =>
                css(styles.mobileNavLink, isActive && styles.mobileNavLinkActive)
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={css(styles.mobileActions)}>
          <button
            className={css(styles.ghostButton)}
            onClick={() => openAction(LINKS.adminDashboard)}
          >
            Admin
          </button>
          <button
            className={css(styles.primaryButton)}
            onClick={() => openAction(LINKS.customerWebApp)}
          >
            Order Now
          </button>
        </div>
      </div>
    </header>
  );
}

const styles = StyleSheet.create({
  header: {
    width: '100%',
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: 'rgba(255, 253, 247, 0.98)',
    boxSizing: 'border-box',
    position: 'sticky',
    top: 0,
    zIndex: 20,
    backdropFilter: 'blur(12px)',
  },

  navbar: {
    width: '100%',
    minHeight: '82px',
    padding: '16px 54px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '22px',
    alignItems: 'center',
    boxSizing: 'border-box',

    '@media (max-width: 1020px)': {
      gridTemplateColumns: 'auto auto',
      justifyContent: 'space-between',
      padding: '16px 24px',
    },

    '@media (max-width: 560px)': {
      minHeight: '68px',
      padding: '12px 16px',
    },
  },
  logoButton: {
    border: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },
  logoMark: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    backgroundColor: colors.ink,
    border: `2px solid ${colors.gold}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '@media (max-width: 560px)': {
      width: '44px',
      height: '44px',
    },
  },
  logoImage: {
    width: '38px',
    height: '38px',
    objectFit: 'contain',
    '@media (max-width: 560px)': {
      width: '31px',
      height: '31px',
    },
  },
  logoText: {
    display: 'block',
    fontSize: '1.86rem',
    fontWeight: '900',
    color: colors.ink,
    lineHeight: 1,
    '@media (max-width: 560px)': {
      fontSize: '1.45rem',
    },
  },
  logoSubtext: {
    display: 'block',
    color: '#8C8170',
    fontSize: '0.88rem',
    marginTop: '5px',
    '@media (max-width: 560px)': {
      fontSize: '0.75rem',
      marginTop: '3px',
    },
  },
  desktopNav: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '22px',
    alignItems: 'center',

    '@media (max-width: 1020px)': {
      display: 'none',
    },
  },
  navLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  navLink: {
    color: colors.ink,
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: '900',
    padding: '10px 12px',
    borderRadius: '8px',

    ':hover': {
      backgroundColor: colors.goldSoft,
    },
  },
  navLinkActive: {
    backgroundColor: colors.goldSoft,
    color: colors.ink,
  },
  navActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    flexWrap: 'wrap',
  },
  menuButton: {
    display: 'none',
    width: '46px',
    height: '42px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '5px',
    padding: 0,

    '@media (max-width: 1020px)': {
      display: 'flex',
    },
  },
  menuLine: {
    width: '22px',
    height: '2px',
    borderRadius: '999px',
    backgroundColor: colors.ink,
    transition: 'transform 180ms ease, opacity 180ms ease',
  },
  menuLineTop: {
    transform: 'translateY(7px) rotate(45deg)',
  },
  menuLineMiddle: {
    opacity: 0,
  },
  menuLineBottom: {
    transform: 'translateY(-7px) rotate(-45deg)',
  },
  mobileMenu: {
    display: 'none',

    '@media (max-width: 1020px)': {
      display: 'block',
      maxHeight: 0,
      overflow: 'hidden',
      opacity: 0,
      transition: 'max-height 220ms ease, opacity 160ms ease',
      borderTop: `1px solid ${colors.border}`,
      backgroundColor: colors.sand,
    },
  },
  mobileMenuOpen: {
    '@media (max-width: 1020px)': {
      maxHeight: '520px',
      opacity: 1,
    },
  },
  mobileNavLinks: {
    display: 'grid',
    gap: '8px',
    padding: '16px 24px 10px',

    '@media (max-width: 560px)': {
      padding: '14px 16px 8px',
    },
  },
  mobileNavLink: {
    color: colors.ink,
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: '900',
    padding: '13px 12px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
  },
  mobileNavLinkActive: {
    backgroundColor: colors.goldSoft,
    borderColor: colors.gold,
  },
  mobileActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    padding: '0 24px 18px',

    '@media (max-width: 560px)': {
      gridTemplateColumns: '1fr',
      padding: '0 16px 16px',
    },
  },
  ghostButton: {
    padding: '11px 16px',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    color: colors.ink,
    fontSize: '0.92rem',
    fontWeight: '900',
    cursor: 'pointer',
  },
  primaryButton: {
    padding: '11px 16px',
    borderRadius: '8px',
    border: `1px solid ${colors.ink}`,
    backgroundColor: colors.ink,
    color: colors.white,
    fontSize: '0.92rem',
    fontWeight: '900',
    cursor: 'pointer',
  },
});

export default Navbar;
